import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import { formatHolidayDate, loggedDayPartLabel, NO_DAY_PART_KEY } from "../../../components/team/Holidays/holidayFormat";
import type { MetaChipTone } from "../../../components/UIToolkit/Badges/MetaChip";
import type { ActivityLogIntegrationName } from "./activityLogIntegrations";
import type { GroupedActivityLogEntry, GroupedActivityLogEntryDetails } from "./useGroupedActivityLogQuery";

/**
 * Shared display vocabulary for the Grouped Activity Log. Lives here — not in a component — so the
 * grid's Action/Channel/What columns and the action-name/channel filters can't drift from each other,
 * mirroring `EmployeeChangelog/shiftLineageDisplay.ts`'s role for that log.
 *
 * The 9 `actionName` values and 3 `channel` values are literal wire values the backend's significance
 * ladder emits (`backend/database/activity_log_grouping.py` — see
 * `docs/activity-log-request-id-grouping-plan.md` section 2) — treated as IDs, not display text, per
 * the FE i18n convention: the enum VALUE never changes, only its rendered label does.
 */

/** The one action-name value the "Bulk Update" sublabel keys off (a group past `BULK_UPDATE_THRESHOLD`). */
export const BULK_UPDATE_ACTION_NAME = "Bulk Update";

/** A holiday added, edited, applied or deleted, or a person's holiday group or day part change; it keeps
 * this name at any size, never Bulk Update. */
export const HOLIDAY_ACTION_NAME = "Holiday";

/** A Holiday entry's details are the holiday's own row, as "Other" details from this table. */
export const HOLIDAY_TABLE_NAME = "department_holiday";

/** "Christmas · Fri, Dec 25, 2026" from the holiday's row. The network layer turns its date into a `DateTime`. */
const holidaySublabel = (changes: Record<string, unknown> | null): string => {
	const name = changes?.name;
	const date = changes?.holiday_date;
	if (typeof name !== "string" || !DateTime.isDateTime(date)) throw new Error("A holiday's Activity Log row has no name or date");
	return `${name} · ${formatHolidayDate(date)}`;
};

/** Significance-ladder order (highest → lowest), except Holiday sits just before Other so the filter lists
 * the schedule actions first; Bulk Update last since it's an override, not a tier. */
export const ACTION_NAME_VALUES = [
	"User Status",
	"Call Off",
	"PTO",
	"Request",
	"Recurring",
	"Shift",
	HOLIDAY_ACTION_NAME,
	"Other",
	BULK_UPDATE_ACTION_NAME,
] as const;

export const ACTION_NAME_LABEL_KEY: Record<string, string> = {
	"User Status": "activityLog.grouped.action.userStatus",
	"Call Off": "activityLog.grouped.action.callOff",
	PTO: "activityLog.grouped.action.pto",
	Request: "activityLog.grouped.action.request",
	Recurring: "activityLog.grouped.action.recurring",
	Shift: "activityLog.grouped.action.shift",
	[HOLIDAY_ACTION_NAME]: "activityLog.grouped.action.holiday",
	Other: "activityLog.grouped.action.other",
	[BULK_UPDATE_ACTION_NAME]: "activityLog.grouped.action.bulkUpdate",
};

export const ACTION_NAME_TONE: Record<string, MetaChipTone> = {
	"User Status": "warning",
	"Call Off": "error",
	PTO: "info",
	Request: "primary",
	Recurring: "info",
	Shift: "neutral",
	[HOLIDAY_ACTION_NAME]: "success",
	Other: "neutral",
	[BULK_UPDATE_ACTION_NAME]: "warning",
};

export const CHANNEL_VALUES = ["SMS", "Call", "App"] as const;

export const CHANNEL_LABEL_KEY: Record<string, string> = {
	SMS: "activityLog.grouped.channel.sms",
	Call: "activityLog.grouped.channel.call",
	App: "activityLog.grouped.channel.app",
};

/** The holiday `what` whose values are day parts; the other two holiday `what`s carry group names. */
const HOLIDAY_DAY_PART_CHANGED_WHAT = "Holiday Day Part Changed";

/** A person's holiday group or day part (tier 0): templates whose {{from}}/{{to}} `composeWhatText` fills. */
const HOLIDAY_WHAT_LABEL_KEY: Record<string, string> = {
	"Holiday Group Changed": "activityLog.grouped.what.holidayGroupChanged",
	"Holiday Group Set": "activityLog.grouped.what.holidayGroupSet",
	[HOLIDAY_DAY_PART_CHANGED_WHAT]: "activityLog.grouped.what.holidayDayPartChanged",
};

/**
 * The `what` field's known CLOSED-set values, treated as IDs, same convention as `actionName`. Covers
 * the request-creation ask (`requests`-representative, tier 4, change_type=Insert), the standalone
 * `requests`-column outcome (tier 4, an Update with no requests_status sibling), the businessy
 * `requests_status`-representative outcome (tier 4, 2026-09-06 — replaces the old generic "Request
 * Status Updated - Approved" composition), PTO's plain change-type label (tier 3, 2026-09-06 — the
 * date/duration moved to `details`), and the recurring-shift/shift change types (tiers 5/6) — all fixed,
 * exhaustive enums on the backend (`_row_to_entry` in `backend/database/activity_log_grouping.py`).
 *
 * Deliberately does NOT cover: Call Off's `what` (tier 2, the real free-text call-off reason —
 * `CALL_OFF_REASONS` explicitly doesn't cover 100% of real values); or a requests_status row whose
 * request_type the backend's own join couldn't resolve, which falls back to the OLD generic composition
 * ("Request Status Updated - Approved") — a defensive backend-only path, not expected in practice.
 * `whatLabel` below falls back to rendering any of these as-is when they aren't in this map, which is
 * correct, intended behavior here too (same convention already accepted for `performedBy`'s composed
 * strings, section 7 of the grouping plan).
 */
export const WHAT_LABEL_KEY: Record<string, string> = {
	// Request creation — worded as the ask, not a completed outcome (a bare creation with zero
	// responses previously read as "Pickup Request"/"Replace"/"Swap", misleadingly implying it was
	// already fulfilled). "Replacement Requested"/"Swap Requested" are reused, unmodified, by the
	// businessy requests_status composition below (a still-pending status reads with the exact same
	// wording as the original ask) — deliberately not duplicated as separate keys.
	"Open Shift Posted": "activityLog.grouped.what.openShiftPosted",
	"Replacement Requested": "activityLog.grouped.what.replacementRequested",
	"Swap Requested": "activityLog.grouped.what.swapRequested",
	// Standalone `requests`-column outcome (an Update with no requests_status sibling in the same
	// transaction, e.g. a cancellation) — a separate vocabulary from the requests_status combos above.
	"Request Updated": "activityLog.grouped.what.requestUpdated",
	"Request Fulfilled": "activityLog.grouped.what.requestFulfilled",
	"Request Canceled": "activityLog.grouped.what.requestCanceled",
	// A posting that raised an already-open matching request's needed count instead of (or as well as)
	// creating a new one (APP-875).
	"Open Shift Posted (Added to Existing)": "activityLog.grouped.what.openShiftPostedAddedToExisting",
	// Businessy Request-status outcome (tier 4, requests_status-representative, 2026-09-06) — "Requested"
	// covered above (reused); the Approved/Declined/Closed verbs are new per noun (Closed: APP-875).
	"Shift Pickup Requested": "activityLog.grouped.what.shiftPickupRequested",
	"Shift Pickup Approved": "activityLog.grouped.what.shiftPickupApproved",
	"Shift Pickup Declined": "activityLog.grouped.what.shiftPickupDeclined",
	"Shift Pickup Closed": "activityLog.grouped.what.shiftPickupClosed",
	"Replacement Approved": "activityLog.grouped.what.replacementApproved",
	"Replacement Declined": "activityLog.grouped.what.replacementDeclined",
	"Replacement Closed": "activityLog.grouped.what.replacementClosed",
	"Swap Approved": "activityLog.grouped.what.swapApproved",
	"Swap Declined": "activityLog.grouped.what.swapDeclined",
	"Swap Closed": "activityLog.grouped.what.swapClosed",
	// Recurring shift change type — plus the two mixed-transaction labels (APP-875): an add or removal
	// that also updated existing patterns.
	"Recurring Shift Added": "activityLog.grouped.what.recurringShiftAdded",
	"Recurring Shift Updated": "activityLog.grouped.what.recurringShiftUpdated",
	"Recurring Shift Removed": "activityLog.grouped.what.recurringShiftRemoved",
	"Recurring Shift Added (Updated)": "activityLog.grouped.what.recurringShiftAddedUpdated",
	"Recurring Shift Removed (Updated)": "activityLog.grouped.what.recurringShiftRemovedUpdated",
	// Shift change type.
	"Shift Added": "activityLog.grouped.what.shiftAdded",
	"Shift Updated": "activityLog.grouped.what.shiftUpdated",
	"Shift Removed": "activityLog.grouped.what.shiftRemoved",
	// PTO change type (tier 3, 2026-09-06) — now a plain label; the date range/duration moved to `details`.
	"PTO Added": "activityLog.grouped.what.ptoAdded",
	"PTO Updated": "activityLog.grouped.what.ptoUpdated",
	"PTO Removed": "activityLog.grouped.what.ptoRemoved",
	// Holiday (tier 0): what happened to the holiday that heads the entry.
	"Holiday Added": "activityLog.grouped.what.holidayAdded",
	"Holiday Updated": "activityLog.grouped.what.holidayUpdated",
	"Holiday Applied": "activityLog.grouped.what.holidayApplied",
	"Holiday Deleted": "activityLog.grouped.what.holidayDeleted",
	...HOLIDAY_WHAT_LABEL_KEY,
};

/** A logged holiday group: its name, or Not assigned when the person has none. */
const holidayGroupValue = (group: string | null, t: TFunction): string => group ?? t("holidays.group.notAssigned");

/** A logged holiday day part in words, or No day part when there is none (the person has no group). */
const holidayDayPartValue = (dayPart: string | null, t: TFunction): string => (dayPart === null ? t(NO_DAY_PART_KEY) : loggedDayPartLabel(t, dayPart));

/** One logged holiday field: its value before and after. */
type LoggedHolidayChange = { old: string | null; new: string | null };

type LoggedHolidayChanges = { holiday_group?: LoggedHolidayChange; holiday_day_part?: LoggedHolidayChange };

/** The holiday fields of a changelog row's `changes` — the one reader of that JSON shape. */
const loggedHolidayChanges = (changes: Record<string, unknown>): LoggedHolidayChanges =>
	// The holiday changelog trigger writes one or both of these keys, each as {old, new}.
	changes as LoggedHolidayChanges;

/** A Holiday headline: its `what` names the field, and the headline row's raw `changes` (on Other's
 * details shape, so the API has no holiday-only kind) hold the values. */
const composeHolidayWhat = (what: string, details: GroupedActivityLogEntryDetails | null | undefined, t: TFunction): string => {
	if (details?.kind !== "Other" || details.changes === null) throw new Error(`Holiday activity "${what}" has no changes`);
	const isDayPart = what === HOLIDAY_DAY_PART_CHANGED_WHAT;
	const logged = loggedHolidayChanges(details.changes);
	const change = isDayPart ? logged.holiday_day_part : logged.holiday_group;
	if (change === undefined) throw new Error(`Holiday activity "${what}" has no ${isDayPart ? "day part" : "group"} change`);
	const formatValue = isDayPart ? holidayDayPartValue : holidayGroupValue;
	return t(HOLIDAY_WHAT_LABEL_KEY[what], { from: formatValue(change.old, t), to: formatValue(change.new, t) });
};

/**
 * The outer grid's root "What" cell text (2026-09-07 fix, extracted as a pure, exported function —
 * mirroring `composeRowWhatText`'s own extraction below — so this composition is directly unit-testable
 * without mounting `GroupedActivityLog.tsx`). Call Off's (tier 2) own `what` is just the closed-set
 * change-type verb ("Shift Removed"/"Shift Updated" — same wording tier 6 already uses, already
 * translated via `WHAT_LABEL_KEY` above). The real call-off reason is genuinely unconstrained free text
 * (`CALL_OFF_REASONS`, backend, doesn't cover every real value, and departments can add their own custom
 * labels), so it can never be a closed-set, translatable value — composed onto the translated verb
 * CLIENT-SIDE instead (reverting a prior pass that baked this composition into one opaque backend string,
 * which broke translation for the verb half entirely: the composed string could never match a
 * `WHAT_LABEL_KEY` entry, so it fell through to the raw, untranslated fallback). Reused by both the "What"
 * column's own render and its search index so a user can still search by either half.
 */
export const composeWhatText = (what: string | null | undefined, details: GroupedActivityLogEntryDetails | null | undefined, t: TFunction): string => {
	if (what && what in HOLIDAY_WHAT_LABEL_KEY) return composeHolidayWhat(what, details, t);
	const label = what ? t(WHAT_LABEL_KEY[what] ?? what) : "—";
	return details?.kind === "Call Off" && details.reason ? `${label} - ${details.reason}` : label;
};

/** The fields `composePerformedForLabel` reads — structurally compatible with
 * `ResolvedGroupedActivityLogEntry` (`useGroupedActivityLogQuery.ts`), declared here rather than imported
 * for the same no-circular-import reason as `RowWhatComposeInput` below. */
export interface PerformedForComposeInput {
	performedForName: string | null;
	performedFor: number | null;
	swapOutNurseId: number | null;
	swapInNurseId: number | null;
	replacedNurseId?: number | null;
	replacingNurseId?: number | null;
	affectedNurseCount: number | null;
}

const isPresent = (value: number | null | undefined): value is number => value !== null && value !== undefined;

/**
 * The grid's Performed For text — one pure function so the cell, its sort and its search index can't
 * disagree about what a row shows. Checked in order, each case mutually exclusive with the next by
 * construction on the backend:
 *  1. `performedForName` — a hard-deleted employee, whose `performedFor` is necessarily null. Already a
 *     display name: it bypasses `getNurseFormattedName`, which cannot resolve a nurse that no longer exists.
 *  2. `performedFor` — the single nurse the group is about.
 *  3. The swap pair (out → in).
 *  4. The approved-replacement pair (replaced → replacing), in the same "{{out}} → {{in}}" wording as a
 *     swap: both read as one nurse's shift going to another.
 *  5. `affectedNurseCount` — a group that genuinely spans many nurses (Bulk-Update-sized), where one
 *     name would misrepresent the transaction.
 */
export const composePerformedForLabel = (entry: PerformedForComposeInput, getNurseFormattedName: (nurseId: number) => string, t: TFunction): string => {
	if (entry.performedForName) return entry.performedForName;
	if (isPresent(entry.performedFor)) return getNurseFormattedName(entry.performedFor);
	if (isPresent(entry.swapOutNurseId) && isPresent(entry.swapInNurseId)) {
		return t("activityLog.grouped.performedFor.swap", { out: getNurseFormattedName(entry.swapOutNurseId), in: getNurseFormattedName(entry.swapInNurseId) });
	}
	if (isPresent(entry.replacedNurseId) && isPresent(entry.replacingNurseId)) {
		return t("activityLog.grouped.performedFor.swap", { out: getNurseFormattedName(entry.replacedNurseId), in: getNurseFormattedName(entry.replacingNurseId) });
	}
	if (isPresent(entry.affectedNurseCount)) return t("activityLog.grouped.performedFor.nurseCount", { count: entry.affectedNurseCount });
	return "—";
};

/**
 * `ActivityLogGroupRow.rowWhat`'s closed-set values (2026-09-07) — which, if any, business-sentence
 * template the detail grid's "Details" column (`GroupDetailPanel.tsx`'s `detailsText`) should compose for
 * a row, instead of the generic `[objectType, reason, sublabel]` fragments. Two composition STYLES, per
 * this file's own `composeRowWhatText`:
 *  - chip style (`OpenShiftPosted`/`ReplacementRequested`/`SwapRequested`/`ShiftCreated`): a short,
 *    pre-translated leading label joined with " · " onto range/job/needed, same join-style already used
 *    by `formatRequestDetailSublabel`/`rowDetailSublabel` elsewhere in this feature.
 *  - prose style (`RequestStatusPending/Approved/Declined`, `RequestObjectUpdated/Fulfilled/Canceled`): a
 *    real i18n TEMPLATE with named placeholders (`statusProse`/`objectProse` below) — word order and
 *    prepositions differ across languages, so only a template (not string concatenation) can get this
 *    right in every locale, same reasoning `formatRecurringRecurrenceLabel`'s own `onDay` template
 *    already established for this file.
 * See `backend/database/activity_log_grouping.py`'s `_row_business_sentence_kind` for the authoritative
 * dispatch this mirrors.
 */
export const ROW_WHAT_CHIP_LABEL_KEY: Record<string, string> = {
	OpenShiftPosted: "activityLog.grouped.detail.rowWhat.openShiftPosted",
	ReplacementRequested: "activityLog.grouped.detail.rowWhat.replacementRequested",
	SwapRequested: "activityLog.grouped.detail.rowWhat.swapRequested",
	ShiftCreated: "activityLog.grouped.detail.rowWhat.shiftCreated",
};

/** Prose verb for a `requests_status` row's own ask/response outcome (pending/approved/declined/closed)
 * — the "{{verb}} {{noun}} request of {{range}} for {{job}}" template (`statusProse` below). */
export const ROW_WHAT_STATUS_VERB_LABEL_KEY: Record<string, string> = {
	RequestStatusPending: "activityLog.grouped.detail.rowWhat.pending",
	RequestStatusApproved: "activityLog.grouped.detail.rowWhat.approved",
	RequestStatusDeclined: "activityLog.grouped.detail.rowWhat.declined",
	RequestStatusClosed: "activityLog.grouped.detail.rowWhat.closed",
};

/** Prose verb for a standalone `requests` Update row's own state (no `requests_status` sibling needed —
 * this is the `requests` row's own `status` column) — the "{{verb}} {{noun}} of {{range}} for {{job}}"
 * template (`objectProse` below). */
export const ROW_WHAT_OBJECT_VERB_LABEL_KEY: Record<string, string> = {
	RequestObjectUpdated: "activityLog.grouped.detail.rowWhat.updated",
	RequestObjectFulfilled: "activityLog.grouped.detail.rowWhat.fulfilled",
	RequestObjectCanceled: "activityLog.grouped.detail.rowWhat.canceled",
};

/** `requests.request_type`/`requests_status`-joined `request_type` → the noun both prose templates above
 * interpolate ("shift pickup"/"replacement"/"swap", 2026-09-07 fix — see below) — one shared map for both
 * templates (English uses the same noun in either grammatical slot; a locale where that isn't true can
 * still diverge per-key since these are two separate i18n keys under the hood via
 * `statusProse`/`objectProse`, just fed the same source noun here). `replace_shift`/`replace_agency`
 * share one entry, same normalization `_REQUEST_TYPE_NOUN` already uses on the backend for the
 * group-level `what`.
 *
 * `open_shift`'s noun (2026-09-07 fix, was a separately-invented "open shift") now reuses the SAME
 * "Shift Pickup" vocabulary `_REQUEST_TYPE_NOUN` (backend, `activity_log_grouping.py`) and this file's
 * own `WHAT_LABEL_KEY` already use for the outer grid's tier-4 headline (e.g. "Shift Pickup Approved") —
 * lowercased/de-capitalized to sit naturally mid-sentence in the prose templates above ("Approved shift
 * pickup request of..."). Before this fix the SAME underlying event read "Shift Pickup Approved" in the
 * outer grid but "Approved open shift request of..." one click away in the detail panel — real,
 * user-visible terminology drift within one feature. `replace_shift`/`replace_agency` → "replacement" and
 * `swap` → "swap" already matched `_REQUEST_TYPE_NOUN`'s own wording and are unchanged. */
export const REQUEST_TYPE_NOUN_LABEL_KEY: Record<string, string> = {
	open_shift: "activityLog.grouped.detail.rowWhat.noun.shiftPickup",
	replace_shift: "activityLog.grouped.detail.rowWhat.noun.replacement",
	replace_agency: "activityLog.grouped.detail.rowWhat.noun.replacement",
	swap: "activityLog.grouped.detail.rowWhat.noun.swap",
};

/** Shared shape for the request/open-shift detail sublabel — used, per-row, on
 * `ActivityLogGroupRow.details`/`changes` (detail grid, `GroupDetailPanel.tsx`) — a table-driven,
 * per-row concern distinct from the outer grid's own `GroupedActivityLogEntry.details`, which is now a
 * real discriminated union (`RequestDetails` in `useGroupedActivityLogQuery.ts`; structurally compatible
 * with this shape) — see `formatRequestDetailSublabel`'s doc comment. */
export interface RequestDetailFields {
	shift_start?: string | null;
	shift_end?: string | null;
	location?: string | null;
	job?: string | null;
	needed_nurses?: number | null;
	/** `requests.request_type` (2026-09-07) — only ever populated on a `requests_status`-table detail
	 * row's own `details` (`ActivityLogGroupRow.details`, joined back via the backend's
	 * `RequestStatusRowDetails.request_type`). Drives `rowWhatText`'s noun choice (Shift Pickup /
	 * Replacement / Swap) in `GroupDetailPanel.tsx` — undefined/absent everywhere else this shape is
	 * reused (the group-level `RequestDetails`, and the `requests`-table `changes` spread), which is fine:
	 * those callers never read this field. */
	request_type?: string | null;
}

/**
 * Shared "shift range + location + job + needed" sublabel line for the request/open-shift detail shape —
 * one formatter so the outer grid's "What" column (`GroupedActivityLog.tsx`) and the detail grid's
 * "Details" column (`GroupDetailPanel.tsx`, `requests`/`requests_status` rows) can't drift.
 * `neededNursesLabel` is pre-translated (e.g. "2 needed") by the caller, same "caller owns translation"
 * convention as this file's other formatters (`formatShiftDetailRange`).
 */
export const formatRequestDetailSublabel = (details: RequestDetailFields, timezone: string, neededNursesLabel: string | null): string | null => {
	const range = formatShiftDetailRange(details.shift_start ?? null, details.shift_end ?? null, timezone);
	return [range, details.location, details.job, neededNursesLabel].filter(Boolean).join(" · ") || null;
};

/**
 * Full date range (year included — 2026-09-02 fix: a bare "Sep 2" is ambiguous once the log spans more
 * than one year) for a shift's start/end datetime range: "Sep 2, 2026, 12:00 PM–12:30 AM" when start/end
 * share a day (the common case), otherwise both dates spelled out so an overnight/multi-day shift
 * doesn't read as same-day. A single bound (the other missing) renders alone. Shared pure function (no
 * hook dependency) so both the outer grid's "What" shift-detail sublabel and the detail grid's own
 * "What" column (`GroupDetailPanel.tsx`, `nurse_in_shift` rows) format identically.
 *
 * 12-hour `h:mm a` (2026-09-07 fix, was 24-hour `HH:mm`) — matches this app's existing shift-time
 * convention (`ShiftHoursDisplayAndPicker.tsx`'s own `.toFormat("h:mm a")`), not a new format.
 */
export const formatShiftDetailRange = (start: string | null, end: string | null, timezone: string): string | null => {
	const startDt = start ? DateTime.fromISO(start).setZone(timezone) : null;
	const endDt = end ? DateTime.fromISO(end).setZone(timezone) : null;
	if (startDt && endDt) {
		return startDt.hasSame(endDt, "day")
			? `${startDt.toFormat("MMM d, yyyy, h:mm a")}–${endDt.toFormat("h:mm a")}`
			: `${startDt.toFormat("MMM d, yyyy, h:mm a")} – ${endDt.toFormat("MMM d, yyyy, h:mm a")}`;
	}
	return (startDt ?? endDt)?.toFormat("MMM d, yyyy, h:mm a") ?? null;
};

/** Input shape `composeRowWhatText` needs to compose a detail row's business sentence — structurally
 * compatible with `ActivityLogGroupRow` (`useActivityLogGroupDetailQuery.ts`), declared locally here
 * instead of imported from there to avoid a circular import (that file already imports
 * `RequestDetailFields` from this one). */
export interface RowWhatComposeInput {
	rowWhat: string | null;
	changes: Record<string, unknown> | null;
	details: RequestDetailFields | null;
}

/** `rowWhat === "ShiftCreated"` — a bare `nurse_in_shift` Insert. Chip style: a short pre-translated
 * label joined onto the shift's own raw `start_time`/`end_time` (on `row.changes`, an ISO datetime). */
const composeShiftCreatedRowWhat = (row: RowWhatComposeInput, timezone: string, t: TFunction): string | null => {
	const changes = row.changes as { start_time?: string | null; end_time?: string | null } | null;
	const range = formatShiftDetailRange(changes?.start_time ?? null, changes?.end_time ?? null, timezone);
	return [t(ROW_WHAT_CHIP_LABEL_KEY.ShiftCreated), range].filter(Boolean).join(" · ") || null;
};

/** `rowWhat` is a `requests` Insert (Open Shift Posted / Replacement Requested / Swap Requested) — chip
 * style, same job/needed resolution `rowDetailSublabel`'s own REQUESTS_TABLE_NAME branch in
 * `GroupDetailPanel.tsx` already uses (deliberately NOT `location` — the grounded examples omit it from
 * this particular sentence). */
const composeRequestInsertRowWhat = (row: RowWhatComposeInput, timezone: string, t: TFunction, jobNameById: Map<string, string>): string | null => {
	if (!row.rowWhat) return null;
	const changes = row.changes as {
		shift_start?: string | null;
		shift_end?: string | null;
		needed_nurses?: number | null;
		job_id?: string | null;
	} | null;
	if (!changes) return null;
	const range = formatShiftDetailRange(changes.shift_start ?? null, changes.shift_end ?? null, timezone);
	const job = changes.job_id ? (jobNameById.get(changes.job_id) ?? null) : null;
	const neededLabel = changes.needed_nurses != null ? t("activityLog.grouped.what.neededNurses", { count: changes.needed_nurses }) : null;
	return [t(ROW_WHAT_CHIP_LABEL_KEY[row.rowWhat]), range, job, neededLabel].filter(Boolean).join(" · ") || null;
};

/** `rowWhat` is a `requests_status` ask/response outcome (pending/approved/declined) — prose style,
 * sourced from the backend-joined `row.details` (this row's own `changes` carries no job/shift-date of
 * its own). Returns null when the lineage-joined `details` never resolved a job/request_type, so the
 * caller can fall back to the generic composition instead of rendering a broken template. */
const composeRequestStatusRowWhat = (row: RowWhatComposeInput, timezone: string, t: TFunction): string | null => {
	if (!row.rowWhat || !row.details?.job) return null;
	const range = formatShiftDetailRange(row.details.shift_start ?? null, row.details.shift_end ?? null, timezone);
	const nounKey = row.details.request_type ? REQUEST_TYPE_NOUN_LABEL_KEY[row.details.request_type] : undefined;
	if (!range || !nounKey) return null;
	return t("activityLog.grouped.detail.rowWhat.statusProse", {
		verb: t(ROW_WHAT_STATUS_VERB_LABEL_KEY[row.rowWhat]),
		noun: t(nounKey),
		range,
		job: row.details.job,
	});
};

/** `rowWhat` is a standalone `requests` Update (no `requests_status` sibling) — prose style, sourced
 * straight from this row's own `changes` (a `requests` row always carries its own shift/job context). */
const composeRequestObjectRowWhat = (row: RowWhatComposeInput, timezone: string, t: TFunction, jobNameById: Map<string, string>): string | null => {
	if (!row.rowWhat) return null;
	const changes = row.changes as {
		shift_start?: string | null;
		shift_end?: string | null;
		job_id?: string | null;
		request_type?: string | null;
	} | null;
	if (!changes) return null;
	const range = formatShiftDetailRange(changes.shift_start ?? null, changes.shift_end ?? null, timezone);
	const job = changes.job_id ? (jobNameById.get(changes.job_id) ?? null) : null;
	const nounKey = changes.request_type ? REQUEST_TYPE_NOUN_LABEL_KEY[changes.request_type] : undefined;
	if (!range || !job || !nounKey) return null;
	return t("activityLog.grouped.detail.rowWhat.objectProse", {
		verb: t(ROW_WHAT_OBJECT_VERB_LABEL_KEY[row.rowWhat]),
		noun: t(nounKey),
		range,
		job,
	});
};

/** The detail-row templates for a holiday change, one per field. */
export const HOLIDAY_GROUP_ROW_WHAT_KEY = "activityLog.grouped.detail.rowWhat.holidayGroup";
export const HOLIDAY_DAY_PART_ROW_WHAT_KEY = "activityLog.grouped.detail.rowWhat.holidayDayPart";

/** `rowWhat === "HolidayChange"` — one part per field the save changed, the group first:
 * "Holiday group: A → B · Holiday day part: Calculated → Night". */
const composeHolidayRowWhat = (row: RowWhatComposeInput, t: TFunction): string | null => {
	if (!row.changes) return null;
	const { holiday_group: group, holiday_day_part: dayPart } = loggedHolidayChanges(row.changes);
	return (
		[
			group && t(HOLIDAY_GROUP_ROW_WHAT_KEY, { from: holidayGroupValue(group.old, t), to: holidayGroupValue(group.new, t) }),
			dayPart && t(HOLIDAY_DAY_PART_ROW_WHAT_KEY, { from: holidayDayPartValue(dayPart.old, t), to: holidayDayPartValue(dayPart.new, t) }),
		]
			.filter(Boolean)
			.join(" · ") || null
	);
};

/**
 * Real composed business SENTENCES (2026-09-07) for the detail grid's "Details" column
 * (`GroupDetailPanel.tsx`'s `detailsText`), keyed by `row.rowWhat` — a closed-set ID (see
 * `ROW_WHAT_CHIP_LABEL_KEY`/`ROW_WHAT_STATUS_VERB_LABEL_KEY`/`ROW_WHAT_OBJECT_VERB_LABEL_KEY`/
 * `REQUEST_TYPE_NOUN_LABEL_KEY`'s own doc comments above for the full vocabulary and which values are
 * grounded vs. reasonably extrapolated from real dev data). Two composition styles, one small named
 * helper per branch above:
 *  - chip (`composeShiftCreatedRowWhat`/`composeRequestInsertRowWhat`): a short leading label joined with
 *    " · " onto range/job/needed (no location — matches the grounded examples exactly, e.g. "Open shift
 *    posted · Sep 2, 2026, 6:00 AM–6:30 PM · Nurse · 2 needed").
 *  - prose (`composeRequestStatusRowWhat`/`composeRequestObjectRowWhat`): one i18n TEMPLATE with named
 *    placeholders ("{{verb}} {{noun}} [request] of {{range}} for {{job}}"), never string concatenation —
 *    same reasoning `formatRecurringRecurrenceLabel`'s own `onDay` template already established.
 * Returns null (not "—") when `row.rowWhat` is set but this row's own data can't fill the template (e.g.
 * a `requests_status` row whose lineage-joined `details` never resolved) — the caller (`detailsText` in
 * `GroupDetailPanel.tsx`) falls back to the generic composition in that case, same as when `rowWhat`
 * itself is null. Extracted as a pure, standalone function (2026-09-07) so this composition — the exact
 * thing that let Fix 3's terminology drift ship unnoticed with zero test coverage — is directly
 * unit-testable without mounting the component; see `backend/database/activity_log_grouping.py`'s
 * `_row_business_sentence_kind` for the authoritative dispatch this mirrors.
 */
export const composeRowWhatText = (row: RowWhatComposeInput, timezone: string, t: TFunction, jobNameById: Map<string, string>): string | null => {
	if (row.rowWhat === "HolidayChange") return composeHolidayRowWhat(row, t);
	if (row.rowWhat === "ShiftCreated") return composeShiftCreatedRowWhat(row, timezone, t);
	if (row.rowWhat && row.rowWhat in ROW_WHAT_CHIP_LABEL_KEY) return composeRequestInsertRowWhat(row, timezone, t, jobNameById);
	if (row.rowWhat && row.rowWhat in ROW_WHAT_STATUS_VERB_LABEL_KEY) return composeRequestStatusRowWhat(row, timezone, t);
	if (row.rowWhat && row.rowWhat in ROW_WHAT_OBJECT_VERB_LABEL_KEY) return composeRequestObjectRowWhat(row, timezone, t, jobNameById);
	return null;
};

/**
 * Time-of-day-only range for a `recurring_shift` row's own `shift_start`/`shift_end` — Postgres `Time`
 * columns (`backend/database/models.py`, `recurring_shift` table) with NO date and NO timezone, so the
 * raw value is a bare `"HH:mm:ss"` string (e.g. `"07:00:00"`), not an ISO datetime.
 *
 * Deliberately NOT `formatShiftDetailRange`: that formatter does `DateTime.fromISO(value).setZone(...)`,
 * which — fed a bare time string — parses it as an ISO datetime anchored to TODAY's date in the JS
 * runtime's local zone, then re-projects that into the department timezone. There's no real instant here
 * to convert (a recurring weekly pattern has no single underlying date), so that path silently rendered a
 * different, wrong date on every page load depending on what day it happened to be viewed (confirmed bug,
 * 2026-09-06). This formatter never parses a date component and never re-projects a zone: `{ zone }` on
 * `fromFormat` only anchors the parsed wall-clock digits so `.toFormat` has a zone to format against (the
 * value has no zone of its own to convert FROM, so this cannot shift the displayed hour/minute) — no date
 * is ever read out of the result.
 *
 * No day-of-week is rendered here — `recurring_shift.changes` (the audit snapshot, `to_jsonb(NEW)` — see
 * `log_recurring_shift_to_changelog` in `backend/tests/db_triggers.py`) does carry the cadence, nested
 * inside `recurring_params` (JSONB) as `recurring_type`/`weekdays`, but that shape differs per recurrence
 * type (Weekly/Daily/ConsecutiveDays — `backend/models/enums.py`'s `RecurringType`) — there's no single
 * top-level "day" field to read directly, so rendering it is a separate concern from this formatter's own
 * time-of-day range. See `formatRecurringRecurrenceLabel` below (2026-09-06) for that piece.
 *
 * 12-hour `h:mm a` for the OUTPUT (2026-09-07 fix, was 24-hour `HH:mm`) — matches this app's existing
 * shift-time convention (`ShiftHoursDisplayAndPicker.tsx`'s own `.toFormat("h:mm a")`). The `"HH:mm:ss"`
 * PARSE format below is unrelated and untouched — it's reading the raw Postgres `Time` column shape, not
 * choosing how the value renders.
 */
export const formatRecurringShiftTimeRange = (start: string | null, end: string | null, timezone: string): string | null => {
	const parseTime = (value: string | null): DateTime | null => {
		if (!value) return null;
		const parsed = DateTime.fromFormat(value, "HH:mm:ss", { zone: timezone });
		return parsed.isValid ? parsed : null;
	};
	const startDt = parseTime(start);
	const endDt = parseTime(end);
	if (startDt && endDt) return `${startDt.toFormat("h:mm a")}–${endDt.toFormat("h:mm a")}`;
	return (startDt ?? endDt)?.toFormat("h:mm a") ?? null;
};

/** `recurring_shift.changes.recurring_params.weekdays` entries (lowercase, e.g. `"tuesday"`) mapped to
 * the i18n key for its short, translated abbreviation — same "closed-set ID -> i18n key" convention as
 * `OBJECT_TYPE_LABEL_KEY` below, not raw display text (2026-09-06 fix: this used to be hardcoded English
 * `"Sun"`…`"Sat"`, never translated). Used for every Weekly row regardless of how many weekdays it
 * carries — a single weekday and a multi-weekday `/`-join (the rarer shape a row's own `weekdays` array
 * can carry, confirmed against real dev data) both resolve through this same map. */
export const WEEKDAY_ABBREVIATION_LABEL_KEY: Record<string, string> = {
	sunday: "activityLog.grouped.detail.recurrence.weekday.sunday",
	monday: "activityLog.grouped.detail.recurrence.weekday.monday",
	tuesday: "activityLog.grouped.detail.recurrence.weekday.tuesday",
	wednesday: "activityLog.grouped.detail.recurrence.weekday.wednesday",
	thursday: "activityLog.grouped.detail.recurrence.weekday.thursday",
	friday: "activityLog.grouped.detail.recurrence.weekday.friday",
	saturday: "activityLog.grouped.detail.recurrence.weekday.saturday",
};

/** `recurring_shift.changes.recurring_params.recurring_type` real-world values mapped to the i18n key for
 * its translated label — mirrors the backend's closed `RecurringType` enum (`backend/models/enums.py`:
 * Daily/Weekly/ConsecutiveDays, no others exist). Same "closed-set ID -> i18n key" convention as
 * `OBJECT_TYPE_LABEL_KEY` below (2026-09-06 fix: this used to be hardcoded English `"Daily"`/`"Weekly"`/
 * `"Consecutive Days"`, never translated). Drives `formatRecurringRecurrenceLabel` below. */
export const RECURRING_TYPE_LABEL_KEY: Record<string, string> = {
	Daily: "activityLog.grouped.detail.recurrence.type.daily",
	Weekly: "activityLog.grouped.detail.recurrence.type.weekly",
	ConsecutiveDays: "activityLog.grouped.detail.recurrence.type.consecutiveDays",
};

/**
 * Human-readable recurrence description for a `recurring_shift` row's own `changes.recurring_params` —
 * e.g. "Weekly on Tuesday" for the common single-weekday shape, or "Weekly on Tue/Thu" (abbreviated,
 * `/`-joined) for the rarer multi-weekday shape a single row's own `weekdays` array can carry (both
 * confirmed against real dev data). `Daily`/`ConsecutiveDays` render as a plain type label with no
 * weekday suffix (`weekdays` doesn't apply to either). Falls back to the raw `recurring_type`/weekday
 * value, unbounded, for anything the two maps above don't cover — same fallback convention this file's
 * other closed-set maps use (see `objectTypeLabel` in `GroupDetailPanel.tsx`) — rather than guessing at
 * wording for a value that isn't one of the known ones. `null` when there's no `recurring_params` at
 * all, or no usable `recurring_type` in it.
 *
 * Takes `t` as a parameter rather than calling `useTranslation()` itself — this file's own "caller owns
 * translation" convention (see `formatShiftDetailRange`/`formatRequestDetailSublabel`'s doc comments for
 * the same precedent), which also keeps this a plain, synchronous, easily-unit-tested function. The
 * "type + weekday" composition is one real i18n template with named placeholders
 * (`activityLog.grouped.detail.recurrence.onDay`), NOT string concatenation of two separately-translated
 * pieces — word order and prepositions differ across languages, so only a template can get this right in
 * every locale.
 */
export const formatRecurringRecurrenceLabel = (recurringParams: Record<string, unknown> | null | undefined, t: TFunction): string | null => {
	if (!recurringParams) return null;
	const recurringType = recurringParams.recurring_type;
	if (typeof recurringType !== "string") return null;
	const typeLabel = t(RECURRING_TYPE_LABEL_KEY[recurringType] ?? recurringType);
	if (recurringType !== "Weekly") return typeLabel;

	const weekdays = recurringParams.weekdays;
	if (!Array.isArray(weekdays) || weekdays.length === 0) return typeLabel;
	const namedWeekdays = weekdays.filter((day): day is string => typeof day === "string");
	if (namedWeekdays.length === 0) return typeLabel;
	const dayLabel = namedWeekdays.map((day) => t(WEEKDAY_ABBREVIATION_LABEL_KEY[day] ?? day)).join("/");
	return t("activityLog.grouped.detail.recurrence.onDay", { type: typeLabel, day: dayLabel });
};

/** When an entry was written, in the department's timezone. */
export const formatActivityLogWhen = (value: string, timezone: string): string => DateTime.fromISO(value).setZone(timezone).toFormat("MMM d, yyyy, h:mm a");

/**
 * The "What" sublabel: the shift, PTO, request or recurring pattern the entry is about, per `details.kind`.
 * PTO's strings come pre-formatted; a recurring pattern's times are bare `Time` strings, not ISO datetimes.
 */
export const composeWhatSublabel = (details: GroupedActivityLogEntryDetails | null, timezone: string, t: TFunction): string | null => {
	if (details === null) return null;
	switch (details.kind) {
		case "Call Off":
		case "Shift": {
			const range = formatShiftDetailRange(details.shift_start, details.shift_end, timezone);
			return [range, details.location].filter(Boolean).join(" · ") || null;
		}
		case "PTO":
			return [details.date_range, details.duration].filter(Boolean).join(" · ") || null;
		case "Request": {
			const neededLabel = details.needed_nurses != null ? t("activityLog.grouped.what.neededNurses", { count: details.needed_nurses }) : null;
			return formatRequestDetailSublabel(details, timezone, neededLabel);
		}
		case "Recurring": {
			const timeRange = formatRecurringShiftTimeRange(details.shift_start, details.shift_end, timezone);
			const recurrenceLabel = formatRecurringRecurrenceLabel(details.recurring_params, t);
			return [timeRange, details.location, recurrenceLabel].filter(Boolean).join(" · ") || null;
		}
		case "Other":
			return details.table_name === HOLIDAY_TABLE_NAME ? holidaySublabel(details.changes) : null;
	}
};

/**
 * Row height (px) for a "What" cell rendering the two-line tier-detail sublabel (primary label +
 * `whatSublabel`). The grid's normal dense row is 36px — enough for the single-line primary
 * label alone, but a second `LABEL_XS` line pushes the stacked content to ~34px with effectively no
 * padding, so its text visually touches the row divider below. 56px reuses MUI's own "comfortable"
 * density row height, which happens to give both lines the same ~8px breathing room the normal
 * single-line row gets at 36px — a deliberate, not arbitrary, choice.
 */
export const WHAT_SUBLABEL_ROW_HEIGHT = 56;

/** `ActivityLogGroupRow.changeType` — same three values as `StaffChangeType` (backend). */
export const CHANGE_TYPE_LABEL_KEY: Record<string, string> = {
	Insert: "activityLog.grouped.detail.changeType.insert",
	Update: "activityLog.grouped.detail.changeType.update",
	Delete: "activityLog.grouped.detail.changeType.delete",
};

export const CHANGE_TYPE_TONE: Record<string, MetaChipTone> = {
	Insert: "success",
	Update: "info",
	Delete: "error",
};

/** The table whose rows hold a nurse's application to a request (pickup, replacement, swap). */
export const REQUEST_STATUS_TABLE_NAME = "requests_status";

/** The change type a status chip never shows on: a removed status is not an outcome, so "Deleted" next to
 * a green "Approved" would read as if something was approved. */
const STATUS_CHIP_EXCLUDED_CHANGE_TYPE = "Delete";

/** Outcome chip shown beside a request-status row's Change Type chip, keyed by the row's own `rowWhat`.
 * Pending is left out on purpose: it is every new application, so it carries no signal there. The labels
 * are the same request-status words the open-shift card's `StatusBadge` shows. */
const REQUEST_STATUS_CHIP: Record<string, { labelKey: string; tone: MetaChipTone }> = {
	RequestStatusApproved: { labelKey: "status.approved", tone: "success" },
	RequestStatusDeclined: { labelKey: "status.declined", tone: "error" },
	RequestStatusClosed: { labelKey: "status.closed", tone: "neutral" },
};

/** The fields `requestStatusChip` reads — structurally compatible with `ActivityLogGroupRow`, declared
 * here for the same no-circular-import reason as `RowWhatComposeInput` above. */
export interface StatusChipInput {
	tableName: string;
	changeType: string;
	rowWhat: string | null;
}

/** The detail grid's outcome chip for a row, or null when it gets none: only a request-status row that
 * set an approved/declined/closed status (an Insert or Update, never a Delete). The base Change Type chip
 * (Created/Updated/Deleted) is unchanged; this sits beside it, like the "Punched" badge. */
export const requestStatusChip = (row: StatusChipInput): { labelKey: string; tone: MetaChipTone } | null => {
	if (row.tableName !== REQUEST_STATUS_TABLE_NAME || row.changeType === STATUS_CHIP_EXCLUDED_CHANGE_TYPE || row.rowWhat === null) return null;
	// DEFAULT: a status with no chip (pending, or one the backend has no rowWhat for) shows only the base chip.
	return REQUEST_STATUS_CHIP[row.rowWhat] ?? null;
};

/** The table/change-type pair that marks a detail row as a real punch (a `nurse_in_shift_actual`
 * Insert) rather than a shift assignment — drives the detail grid's "Punched" badge alongside the
 * unchanged generic "Created" Change Type label (see `GroupDetailPanel.tsx`'s Change Type column). */
export const PUNCH_TABLE_NAME = "nurse_in_shift_actual";
export const PUNCH_CHANGE_TYPE = "Insert";

/**
 * Tables whose `changes` snapshot carries PTO length — mirrors the backend's own `_PTO_HOURS_TABLE`/
 * `_PTO_DATE_RANGE_TABLE` (`backend/database/activity_log_grouping.py`): `employee_specific_preferences`
 * stores it directly as `hours`; `time_off_request` only has date-only `start_date`/`end_date` bounds.
 * Drives the detail grid's "Details" column for a PTO-table row (`GroupDetailPanel.tsx`) — the outer
 * grid's own PTO sublabel doesn't need these, since its `details` (kind "PTO") already arrives with
 * pre-formatted `date_range`/`duration` strings from the backend.
 */
export const PTO_HOURS_TABLE_NAME = "employee_specific_preferences";
export const PTO_DATE_RANGE_TABLE_NAME = "time_off_request";

/** Full calendar date (no time-of-day) for a PTO row's `start_date`/`end_date` — mirrors the backend's
 * `_format_pto_date`: slices the first 10 characters so it works identically for a bare date
 * ("2026-08-10") and a full timestamp ("2026-09-03T12:00:00+00:00"), and parses in UTC so a
 * timestamp-backed date can never shift onto the wrong calendar day the way parsing-then-converting to
 * the department's own timezone could. */
const formatPtoDate = (value: unknown): string | null => {
	if (typeof value !== "string" || !value) return null;
	const parsed = DateTime.fromISO(value.slice(0, 10), { zone: "utc" });
	return parsed.isValid ? parsed.toFormat("MMM d, yyyy") : null;
};

/** Mirrors the backend's `_pto_date_range` — the PTO date span from a row's own `changes`, for either
 * PTO table (both carry `start_date`/`end_date`, just at different precision). */
export const ptoDateRangeFromChanges = (changes: Record<string, unknown> | undefined | null): string | null => {
	const start = formatPtoDate(changes?.start_date);
	const end = formatPtoDate(changes?.end_date);
	if (start && end) return start === end ? start : `${start} – ${end}`;
	return start ?? end;
};

/** Mirrors the backend's `_pto_duration` — table-dependent PTO length: `employee_specific_preferences`
 * carries it directly as `hours`; `time_off_request` only has date bounds, so the length is the
 * inclusive day span between them (matching the backend's own `(end - start).days + 1`). Returns a
 * discriminated shape (never both) so the caller can pick the right already-translated unit — same
 * "caller owns translation" convention as `formatShiftDetailRange`/`formatRequestDetailSublabel`. */
export const ptoDurationFromChanges = (tableName: string, changes: Record<string, unknown> | undefined | null): { hours: number } | { days: number } | null => {
	if (tableName === PTO_HOURS_TABLE_NAME) {
		const hours = changes?.hours;
		return typeof hours === "number" ? { hours } : null;
	}
	if (tableName === PTO_DATE_RANGE_TABLE_NAME) {
		const start = changes?.start_date;
		const end = changes?.end_date;
		if (typeof start !== "string" || typeof end !== "string") return null;
		const startDt = DateTime.fromISO(start.slice(0, 10), { zone: "utc" });
		const endDt = DateTime.fromISO(end.slice(0, 10), { zone: "utc" });
		if (!startDt.isValid || !endDt.isValid) return null;
		return { days: Math.round(endDt.diff(startDt, "days").days) + 1 };
	}
	return null;
};

/**
 * Drives the detail grid's "What" column: translates `ActivityLogGroupRow.objectType` — a backend
 * closed-set ID computed from `table_name` (`_table_object_type` in
 * `backend/database/activity_log_grouping.py`), same "treated as an ID, not display text" convention
 * `WHAT_LABEL_KEY` above already uses. NOT the raw snake_case table name (that's the separate, adjacent
 * "Table" column) and NOT `reason` (an internal tombstone message or a call-off reason already shown as
 * the group's own headline `what` in the outer grid — neither belongs under "What"; `reason` shows,
 * unmodified, under "Details" instead). `objectTypeLabel` below falls back to rendering the value as-is
 * when it isn't in this map — the backend's own fallback for an unmapped table (tier 7 "Other" can carry
 * an arbitrary one) is the raw table_name, so that's what renders, same convention as `WHAT_LABEL_KEY`'s
 * fallback for its own composed/unbounded values.
 */
export const OBJECT_TYPE_LABEL_KEY: Record<string, string> = {
	Nurse: "activityLog.grouped.detail.tableObject.nurse",
	"Nurse Shift": "activityLog.grouped.detail.tableObject.nurseShift",
	PTO: "activityLog.grouped.detail.tableObject.pto",
	Request: "activityLog.grouped.detail.tableObject.request",
	"Request Status": "activityLog.grouped.detail.tableObject.requestStatus",
	"Recurring Shift": "activityLog.grouped.detail.tableObject.recurringShift",
	"PBJ Shift": "activityLog.grouped.detail.tableObject.pbjShift",
	// 4 previously-unmapped tables (2026-09-07 fix) that can appear as detail rows — mirrors the
	// backend's own `_TABLE_OBJECT_TYPE` extension (`backend/database/activity_log_grouping.py`).
	"Census Override": "activityLog.grouped.detail.tableObject.censusOverride",
	"Location Credential": "activityLog.grouped.detail.tableObject.locationCredential",
	"Weekly Preference": "activityLog.grouped.detail.tableObject.weeklyPreference",
	User: "activityLog.grouped.detail.tableObject.user",
	Holiday: "activityLog.grouped.detail.tableObject.holiday",
	"Holiday Group": "activityLog.grouped.detail.tableObject.holidayGroup",
	"Holiday Day Part Hours": "activityLog.grouped.detail.tableObject.holidayDayPartHours",
};

/**
 * Detail-row geometry (px) for the outer grid's row expansion. `getExpandContentHeight` must be
 * SYNCHRONOUS — the detail payload isn't known until the lazy per-row fetch resolves — so this is one
 * fixed constant for every row rather than something content-dependent, sized to comfortably fit the
 * nested grid PLUS the "load all" banner when it's shown (the banner is the only variable content).
 */
export const GROUP_DETAIL_GRID_HEIGHT = 280;
/** `expandedContentSx={{ py: 1.5 }}` on the outer grid — 12px top + 12px bottom. */
const EXPAND_ROW_PADDING = 24;
/** Banner (when shown) + the gap between it and the grid. */
const LOAD_ALL_BANNER_ALLOWANCE = 76;
export const EXPAND_CONTENT_HEIGHT = GROUP_DETAIL_GRID_HEIGHT + EXPAND_ROW_PADDING + LOAD_ALL_BANNER_ALLOWANCE;

/** The glyph an actor cell shows when there is no actor at all — the same dash these cells rendered before first names existed. */
export const ACTOR_EMPTY_LABEL = "—";

/**
 * Who made a change, as the backend sends it: the raw value (`performedBy` / `changedUser`) and — only
 * when that value is a real username the caller may see — the user's first name. Composed values
 * ("Automation", "Call off – …"), masked "support"/"system" buckets and a missing actor have no first name.
 */
export interface ActorNames {
	firstName: string | null | undefined;
	username: string | null | undefined;
	/** The agency behind an agency-integration change (Clipboard, Nursa), shown as a chip beside the actor.
	 * DEFAULT: optional — only the Grouped Activity Log's Performed By can be an integration; every other
	 * actor (the detail grid's Changed By, shift history) has no agency to carry. */
	agencyName?: string | null;
}

/** What an actor cell renders: the visible `label`, and the `tooltip` revealed on hover (`null` = no tooltip). */
export interface ActorDisplay {
	label: string;
	tooltip: string | null;
}

const isMissing = (value: string | null | undefined): value is null | undefined => value === null || value === undefined;

/**
 * A person reads as their first name, with the username (email) one hover away. Every other value —
 * automation labels, masked buckets, a username with no first name on file — renders exactly as it did
 * before first names existed, with no tooltip: the hover must add something, never echo the cell.
 */
export const actorDisplay = ({ firstName, username }: ActorNames): ActorDisplay => {
	if (firstName && !isMissing(username)) return { label: firstName, tooltip: username };
	// DEFAULT: a missing actor renders the dash, unchanged from before first names were added.
	return { label: username ?? ACTOR_EMPTY_LABEL, tooltip: null };
};

/** Search text for an actor cell — the first name AND the username, so typing either one finds the row. */
export const actorSearchText = ({ firstName, username, agencyName }: ActorNames): string => [firstName, username, agencyName].filter(Boolean).join(" ");

/** The integration and agency an agency-integration group's chip names, or `null` when it shows no chip:
 * not an integration's change, or no single agency on file for its workers. */
export const entryAgencyAttribution = (entry: GroupedActivityLogEntry): { integration: ActivityLogIntegrationName; agency: string } | null =>
	entry.kind === "resolved" && entry.integrationName && entry.agencyName ? { integration: entry.integrationName, agency: entry.agencyName } : null;

/**
 * The Performed By actor of a grouped entry — its render, sort and search all read it through this, so
 * they can't disagree about what a row shows. The agency rides along only when the row shows its chip,
 * so search never matches a name the row doesn't display. A still-deferred group has no actor yet.
 */
export const groupedEntryActor = (entry: GroupedActivityLogEntry): ActorNames => {
	if (entry.kind !== "resolved") return { firstName: null, username: null, agencyName: null };
	const attribution = entryAgencyAttribution(entry);
	return { firstName: entry.performedByFirstName, username: entry.performedBy, agencyName: attribution ? attribution.agency : null };
};

/**
 * Orders actors by what the cell shows (first name, else the raw value), with the username as the
 * tie-break so two people sharing a first name keep a stable order, and a missing actor last. Pair it
 * with `sortNullUndefinedLast` on the sort option so a missing actor also stays last on a descending sort.
 */
export const compareActors = (a: ActorNames, b: ActorNames): 1 | 0 | -1 => {
	const aUsername = a.username;
	const bUsername = b.username;
	if (isMissing(aUsername) || isMissing(bUsername)) {
		if (isMissing(aUsername) === isMissing(bUsername)) return 0;
		return isMissing(aUsername) ? 1 : -1;
	}
	const cmp = actorDisplay(a).label.localeCompare(actorDisplay(b).label) || aUsername.localeCompare(bUsername);
	return cmp === 0 ? 0 : cmp > 0 ? 1 : -1;
};
